/**
 * usePushChain — shared continuity hook for Push-Mode chaining.
 * ─────────────────────────────────────────────────────────────
 * Single source of truth for "which push modal is open" + "what's the
 * carried job context." Used by JobsList, JobDetail, WhatNeedsAttentionStrip,
 * Command Dashboard, etc. so close→open transitions happen in one render
 * cycle (no perceptible gap, no toast-only handoffs).
 *
 * Standard:
 *   • Execution actions → modal (continue chain)
 *   • Inspection actions → may navigate (allowed)
 *   • If a step.kind has no mapped opener, dev: warn; prod: navigate fallback + toast
 */
import { useState, useCallback } from "react";
import type { NavigateFunction } from "react-router-dom";
import { toast } from "sonner";
import type { Job, Issue } from "@/data/types";
import type { PushNextStep } from "@/components/quick-actions/PushNextStepPanel";

export type PushModalKind = "ordering" | "reschedule" | "assign" | "resolve" | "permit";

export interface PushChainState {
  ordering: Job | null;
  reschedule: Job | null;
  assign: Job | null;
  /** Resolve carries the issue (not the job) so the modal can render the right one. */
  resolve: Issue | null;
  permit: Job | null;
}

export interface UsePushChainOptions {
  navigate?: NavigateFunction;
  /** Look up an open issue for a job — used when chaining INTO Resolve. */
  findOpenIssueForJob?: (jobId: string) => Issue | null;
}

const isDev = typeof import.meta !== "undefined" && (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;

/**
 * Returns:
 *   • current modal state (ordering/reschedule/assign/resolve/permit)
 *   • setters for direct opens (Jobs list quick-actions)
 *   • `closeAll()` — close every modal in one tick
 *   • `chainTo(step, job)` — atomic close-current + open-next based on PushNextStep
 */
export function usePushChain({ navigate, findOpenIssueForJob }: UsePushChainOptions = {}) {
  const [state, setState] = useState<PushChainState>({
    ordering: null,
    reschedule: null,
    assign: null,
    resolve: null,
    permit: null,
  });

  const closeAll = useCallback(() => {
    setState({ ordering: null, reschedule: null, assign: null, resolve: null, permit: null });
  }, []);

  /** Atomic chain transition. Closes the current modal AND opens the next in one render. */
  const chainTo = useCallback(
    (step: PushNextStep, job: Job) => {
      // Build the next state in one shot — React batches → no flicker.
      setState((prev) => {
        const cleared: PushChainState = {
          ordering: null, reschedule: null, assign: null, resolve: null, permit: null,
        };
        switch (step.kind) {
          case "done":
            return cleared;
          case "assign_crew":
            return { ...cleared, assign: job };
          case "reschedule":
            return { ...cleared, reschedule: job };
          case "view_permit":
            return { ...cleared, permit: job };
          case "generate_po":
            return { ...cleared, ordering: job };
          case "resolve_issue": {
            const top = findOpenIssueForJob?.(job.id) ?? null;
            if (!top) {
              toast.info("No open issue to resolve");
              return cleared;
            }
            return { ...cleared, resolve: top };
          }
          case "navigate": {
            // Inspection action — allowed to leave Push Mode.
            if (navigate) {
              navigate(step.to);
            } else {
              if (isDev) {
                console.warn("[usePushChain] navigate step but no NavigateFunction provided", step);
              }
              toast.error("Couldn't open that view");
            }
            return cleared;
          }
          default: {
            // Unknown step kind — dev warn, prod graceful close.
            if (isDev) {
              console.warn("[usePushChain] unmapped step kind", (step as { kind: string }).kind);
            }
            return cleared;
          }
        }
      });
    },
    [navigate, findOpenIssueForJob],
  );

  return {
    // current state
    ordering: state.ordering,
    reschedule: state.reschedule,
    assign: state.assign,
    resolve: state.resolve,
    permit: state.permit,
    // direct opens (entry points)
    openOrdering: useCallback((j: Job) => setState({ ordering: j, reschedule: null, assign: null, resolve: null, permit: null }), []),
    openReschedule: useCallback((j: Job) => setState({ ordering: null, reschedule: j, assign: null, resolve: null, permit: null }), []),
    openAssign: useCallback((j: Job) => setState({ ordering: null, reschedule: null, assign: j, resolve: null, permit: null }), []),
    openResolve: useCallback((i: Issue) => setState({ ordering: null, reschedule: null, assign: null, resolve: i, permit: null }), []),
    openPermit: useCallback((j: Job) => setState({ ordering: null, reschedule: null, assign: null, resolve: null, permit: j }), []),
    // chain control
    closeAll,
    chainTo,
  };
}
